import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
    MapPinIcon,
    GlobeAltIcon,
    FireIcon,
    ArrowsPointingOutIcon,
    SignalIcon,
    ChartBarIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import {
    getRideClusters,
    getDemandHeatmap,
    getSupplyDemandAnalysis,
} from "../api/liveMonitoringApi";
import type {
    ClusterType,
    ClustersQueryParams,
    HeatmapDataSource,
    HeatmapQueryParams,
    SupplyDemandQueryParams,
    ZoneStatus,
    PlatformStatus,
} from "../types";

// ============================================================================
// Constants
// ============================================================================

const clusterTypeOptions: { value: ClusterType; label: string }[] = [
    { value: "TRIPS", label: "Trips" },
    { value: "PICKUPS", label: "Pickups" },
    { value: "DROPOFFS", label: "Dropoffs" },
    { value: "DRIVERS", label: "Drivers" },
];

const heatmapSourceOptions: { value: HeatmapDataSource; label: string }[] = [
    { value: "BOOKINGS", label: "Bookings" },
    { value: "SEARCHES", label: "Searches" },
    { value: "BOTH", label: "Both" },
];

// ============================================================================
// Badges
// ============================================================================

const ZoneStatusBadge = ({ status }: { status: ZoneStatus }) => {
    const styles: Record<ZoneStatus, string> = {
        CRITICAL_SHORTAGE: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        UNDERSERVED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        BALANCED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        OVERSUPPLIED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        NO_ACTIVITY: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
    };
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${styles[status] || styles.NO_ACTIVITY}`}>
            {status.replace(/_/g, " ")}
        </span>
    );
};

const PlatformStatusBadge = ({ status }: { status: PlatformStatus }) => {
    const styles: Record<PlatformStatus, string> = {
        BALANCED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        UNDERSERVED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        OVERSUPPLIED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        CRITICAL_SHORTAGE: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${styles[status] || "bg-slate-100 text-slate-600"}`}>
            {status}
        </span>
    );
};

// ============================================================================
// Section: Ride Clusters
// ============================================================================

const ClustersSection = () => {
    const [params, setParams] = useState<ClustersQueryParams>({
        clusterType: "TRIPS",
        hoursBack: 6,
    });

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["live-clusters", params],
        queryFn: () => getRideClusters(params),
        refetchInterval: 60_000,
    });

    const clusters = data?.data?.clusters ?? [];

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
                    <GlobeAltIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    Ride Clusters
                </h3>
                <div className="flex items-center gap-2">
                    <select
                        value={params.clusterType ?? "TRIPS"}
                        onChange={(e) => setParams((p) => ({ ...p, clusterType: e.target.value as ClusterType }))}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    >
                        {clusterTypeOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <select
                        value={params.hoursBack ?? 6}
                        onChange={(e) => setParams((p) => ({ ...p, hoursBack: Number(e.target.value) }))}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    >
                        {[1, 3, 6, 12, 24].map((h) => (
                            <option key={h} value={h}>{h}h</option>
                        ))}
                    </select>
                    <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
                        <ArrowPathIcon className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    </Button>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" /></div>
            ) : error ? (
                <p className="text-sm text-rose-500">Failed to load clusters</p>
            ) : clusters.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-400">No clusters found</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Cluster</th>
                                <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Location</th>
                                <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Trips</th>
                                <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Completed</th>
                                <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Active</th>
                                <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Cancelled</th>
                                <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Radius</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {clusters.map((c, i) => (
                                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                    <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{`Cluster ${i + 1}`}</td>
                                    <td className="px-3 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                                        {c.latitude?.toFixed(4)}, {c.longitude?.toFixed(4)}
                                    </td>
                                    <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{c.tripCount}</td>
                                    <td className="px-3 py-2 text-emerald-600 dark:text-emerald-400">{c.completed}</td>
                                    <td className="px-3 py-2 text-blue-600 dark:text-blue-400">{c.active}</td>
                                    <td className="px-3 py-2 text-rose-600 dark:text-rose-400">{c.cancelled}</td>
                                    <td className="px-3 py-2 text-slate-500 dark:text-slate-400">—</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {data?.data && (
                <p className="mt-3 text-xs text-slate-400">
                    Total: {data.data.totalClusters} cluster{data.data.totalClusters !== 1 ? "s" : ""} · Period: {data.data.hoursBack}h
                </p>
            )}
        </div>
    );
};

// ============================================================================
// Section: Demand Heatmap
// ============================================================================

const HeatmapSection = () => {
    const [params, setParams] = useState<HeatmapQueryParams>({
        dataSource: "BOTH",
        hoursBack: 6,
    });

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["live-heatmap", params],
        queryFn: () => getDemandHeatmap(params),
        refetchInterval: 60_000,
    });

    const points = data?.data?.points ?? [];
    const hourly = data?.data?.hourlyDistribution ?? [];

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
                    <FireIcon className="h-5 w-5 text-orange-500" />
                    Demand Heatmap
                </h3>
                <div className="flex items-center gap-2">
                    <select
                        value={params.dataSource ?? "BOTH"}
                        onChange={(e) => setParams((p) => ({ ...p, dataSource: e.target.value as HeatmapDataSource }))}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    >
                        {heatmapSourceOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <select
                        value={params.hoursBack ?? 6}
                        onChange={(e) => setParams((p) => ({ ...p, hoursBack: Number(e.target.value) }))}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    >
                        {[1, 3, 6, 12, 24].map((h) => (
                            <option key={h} value={h}>{h}h</option>
                        ))}
                    </select>
                    <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
                        <ArrowPathIcon className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    </Button>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" /></div>
            ) : error ? (
                <p className="text-sm text-rose-500">Failed to load heatmap data</p>
            ) : (
                <div className="space-y-5">
                    {/* Hot spots table */}
                    {points.length > 0 ? (
                        <div className="overflow-x-auto">
                            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                                Top Demand Points ({data?.data?.totalPoints ?? points.length} total)
                            </p>
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 dark:border-slate-700">
                                        <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Location</th>
                                        <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Count</th>
                                        <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Intensity</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {points.slice(0, 15).map((p, i) => (
                                        <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                            <td className="px-3 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                                                {p.latitude?.toFixed(4)}, {p.longitude?.toFixed(4)}
                                            </td>
                                            <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{p.count}</td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-2 w-16 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                                        <div
                                                            className="h-full rounded-full bg-gradient-to-r from-amber-400 to-rose-500"
                                                            style={{ width: `${(p.intensity ?? 0) * 100}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-xs text-slate-500 dark:text-slate-400">{((p.intensity ?? 0) * 100).toFixed(0)}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="py-6 text-center text-sm text-slate-400">No demand data available</div>
                    )}

                    {/* Hourly distribution */}
                    {hourly.length > 0 && (
                        <div>
                            <p className="mb-3 text-xs font-medium text-slate-500 dark:text-slate-400">Hourly Distribution</p>
                            <div className="flex items-end gap-1" style={{ height: 100 }}>
                                {hourly.map((h, i) => {
                                    const maxCount = Math.max(...hourly.map((x) => x.count ?? 0), 1);
                                    const pct = ((h.count ?? 0) / maxCount) * 100;
                                    return (
                                        <div key={i} className="group relative flex-1" title={`${h.hour}:00 — ${h.count} requests`}>
                                            <div
                                                className="w-full rounded-t bg-gradient-to-t from-primary-600 to-primary-400 transition-all group-hover:from-primary-700 group-hover:to-primary-500"
                                                style={{ height: `${pct}%`, minHeight: h.count ? 4 : 0 }}
                                            />
                                            {i % 3 === 0 && (
                                                <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[9px] text-slate-400">{h.hour}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-5 text-center text-[10px] text-slate-400">Hour of day</div>
                        </div>
                    )}
                </div>
            )}

            {data?.data && (
                <p className="mt-3 text-xs text-slate-400">
                    Source: {data.data.dataSource} · Period: {data.data.hoursBack}h
                </p>
            )}
        </div>
    );
};

// ============================================================================
// Section: Supply-Demand Analysis
// ============================================================================

const SupplyDemandSection = () => {
    const [params, setParams] = useState<SupplyDemandQueryParams>({});

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["live-supply-demand", params],
        queryFn: () => getSupplyDemandAnalysis(params),
        refetchInterval: 30_000,
    });

    const overall = data?.data?.overall;
    const zones = data?.data?.zones ?? [];
    const recommendations: string[] = (data?.data?.zones ?? []).map((z) => z.recommendation).filter((r): r is string => r !== null);

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
                    <ArrowsPointingOutIcon className="h-5 w-5 text-violet-500" />
                    Supply-Demand Analysis
                </h3>
                <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                </Button>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" /></div>
            ) : error ? (
                <p className="text-sm text-rose-500">Failed to load supply-demand data</p>
            ) : (
                <div className="space-y-5">
                    {/* Overall summary */}
                    {overall && (
                        <div className="grid gap-3 sm:grid-cols-4">
                            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
                                <p className="text-xs text-slate-500 dark:text-slate-400">Available Drivers</p>
                                <p className="text-xl font-bold text-slate-900 dark:text-white">{overall.totalAvailableDrivers}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
                                <p className="text-xs text-slate-500 dark:text-slate-400">Total Demand</p>
                                <p className="text-xl font-bold text-slate-900 dark:text-white">{overall.totalDemand}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
                                <p className="text-xs text-slate-500 dark:text-slate-400">Supply/Demand Ratio</p>
                                <p className="text-xl font-bold text-slate-900 dark:text-white">{overall.overallRatio?.toFixed(2)}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
                                <p className="text-xs text-slate-500 dark:text-slate-400">Platform Status</p>
                                <PlatformStatusBadge status={overall.platformStatus} />
                            </div>
                        </div>
                    )}

                    {/* Zones table */}
                    {zones.length > 0 && (
                        <div className="overflow-x-auto">
                            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Zones ({zones.length})</p>
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 dark:border-slate-700">
                                        <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Zone</th>
                                        <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Status</th>
                                        <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Drivers</th>
                                        <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Demand</th>
                                        <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Ratio</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {zones.map((z, i) => (
                                        <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                            <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{`Zone ${i + 1}`}</td>
                                            <td className="px-3 py-2"><ZoneStatusBadge status={z.status} /></td>
                                            <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{z.availableDrivers}</td>
                                            <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{z.demandCount}</td>
                                            <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{z.supplyDemandRatio?.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Recommendations */}
                    {recommendations.length > 0 && (
                        <div>
                            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Recommendations</p>
                            <ul className="space-y-2">
                                {recommendations.map((rec, i) => (
                                    <li key={i} className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                                        <SignalIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                        {rec}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================================================
// Main Component
// ============================================================================

export const LiveAnalyticsRoute = () => {
    return (
        <div className="space-y-8">
            <SupplyDemandSection />
            <ClustersSection />
            <HeatmapSection />
        </div>
    );
};
